import type * as L from "leaflet";
import type { MegamapProject, MegamapTotals } from "./data";

/** Pixel-grid clustering — dependency-free "Google Photos" consolidation. Points that fall
 * into the same `radiusPx`×`radiusPx` screen cell merge into one cluster; as you zoom in the
 * cells cover less ground so clusters split, and at high zoom every project stands alone. */
export interface Cluster {
  id: string;
  lat: number;
  lng: number;
  items: MegamapProject[];
}

export function pixelClusters(
  map: L.Map,
  points: MegamapProject[],
  radiusPx = 62,
): Cluster[] {
  const cells = new Map<string, MegamapProject[]>();
  for (const p of points) {
    if (p.lat == null || p.lng == null) continue;
    const pt = map.latLngToContainerPoint([p.lat, p.lng]);
    const key = `${Math.floor(pt.x / radiusPx)}:${Math.floor(pt.y / radiusPx)}`;
    const arr = cells.get(key);
    if (arr) arr.push(p);
    else cells.set(key, [p]);
  }
  const out: Cluster[] = [];
  for (const [key, items] of cells) {
    let lat = 0;
    let lng = 0;
    for (const it of items) {
      lat += it.lat as number;
      lng += it.lng as number;
    }
    out.push({ id: key, lat: lat / items.length, lng: lng / items.length, items });
  }
  return out;
}

/** Proximity radius that tracks the map zoom: the ground distance a fixed screen gap spans,
 * so zooming out grows regional zones and zooming in shrinks them to local ones. `screenPx` is
 * a bit wider than the cluster cell so zones read as "same neighbourhood", not "same dot". */
export function zoomToThresholdKm(zoom: number, lat = 52, screenPx = 80): number {
  const metersPerPixel = (156543.03392 * Math.cos((lat * Math.PI) / 180)) / 2 ** zoom;
  const km = (metersPerPixel * screenPx) / 1000;
  return Math.max(0.5, Math.min(150, Math.round(km * 10) / 10));
}

const sum = (items: MegamapProject[], f: (p: MegamapProject) => number) =>
  items.reduce((a, p) => a + f(p), 0);

export interface AggregateResult extends MegamapTotals {
  count: number;
  located: number;
  /** WPW un-agreed AND not yet sent to the field (private owners with no WPW handover). */
  private_owners_pending: number;
}

export function aggregate(items: MegamapProject[]): AggregateResult {
  return {
    count: items.length,
    located: items.filter((i) => i.located).length,
    route_length_m: sum(items, (i) => i.route_length_m || 0),
    total_value: sum(items, (i) => i.total_value || 0),
    parcel_count: sum(items, (i) => i.parcel_count),
    private_unagreed_parcels: sum(items, (i) => i.private_unagreed_parcels),
    private_owners_pending: sum(items, (i) => i.private_owners_pending),
    needs_subcontractor: items.filter((i) => i.needs_subcontractor).length,
  };
}

/** Ray-casting point-in-polygon. `ring` is [lng, lat] pairs (GeoJSON order). */
export function pointInPolygon(lng: number, lat: number, ring: [number, number][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const intersect =
      yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

function haversineKm(a: MegamapProject, b: MegamapProject): number {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad((b.lat as number) - (a.lat as number));
  const dLng = toRad((b.lng as number) - (a.lng as number));
  const la1 = toRad(a.lat as number);
  const la2 = toRad(b.lat as number);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/** Proximity "work zones" — single-link clustering: two projects share a zone if they are
 * within `thresholdKm` of any project already in it. Geography-based (not zoom), so it answers
 * "which projects are close enough to hand to one PM / one negotiator?". */
export interface Zone extends Cluster {
  topCoordinator: string | null;
  sharedContractors: string[];
  needsCount: number;
  hasWpwCount: number;
}

export function proximityZones(points: MegamapProject[], thresholdKm: number): Zone[] {
  const located = points.filter((p) => p.located);
  const used = new Set<string>();
  const zones: Zone[] = [];
  for (const seed of located) {
    if (used.has(seed.id)) continue;
    const items = [seed];
    used.add(seed.id);
    let added = true;
    while (added) {
      added = false;
      for (const p of located) {
        if (used.has(p.id)) continue;
        if (items.some((q) => haversineKm(q, p) <= thresholdKm)) {
          items.push(p);
          used.add(p.id);
          added = true;
        }
      }
    }
    zones.push(buildZone(items));
  }
  return zones.sort((a, b) => b.items.length - a.items.length);
}

function buildZone(items: MegamapProject[]): Zone {
  const lat = items.reduce((a, i) => a + (i.lat as number), 0) / items.length;
  const lng = items.reduce((a, i) => a + (i.lng as number), 0) / items.length;

  // Candidate PM = coordinator already on the most projects in the zone.
  const coordFreq = new Map<string, number>();
  for (const it of items) {
    for (const name of it.coordinator_names) {
      coordFreq.set(name, (coordFreq.get(name) ?? 0) + 1);
    }
  }
  let topCoordinator: string | null = null;
  let best = 0;
  for (const [name, n] of coordFreq) {
    if (n > best) {
      best = n;
      topCoordinator = name;
    }
  }

  const sharedContractors = [
    ...new Set(items.flatMap((i) => i.wpw_contractor_names)),
  ].sort();

  return {
    id: items[0].id,
    lat,
    lng,
    items,
    topCoordinator,
    sharedContractors,
    needsCount: items.filter((i) => i.needs_subcontractor).length,
    hasWpwCount: items.filter((i) => i.has_wpw_order).length,
  };
}
